import {
    AlertTriangle,
    CheckCheck,
    CheckCircle2,
    ChevronDown,
    ChevronLeft,
    ChevronUp,
    CircleAlert,
    LoaderCircle,
    MessageCircle,
    RefreshCw,
    Send,
    Users,
} from 'lucide-react'
import React, { useCallback, useContext, useEffect, useState } from 'react'
import { FaCircleUser } from 'react-icons/fa6'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { BASE_URL_API } from '../../References/URLs'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import AuthContext from '../../store/auth-context'
import styles from './PayslipDeliveryStatusScreen.module.css'

// ─── Constants ────────────────────────────────────────────────────────────────

const DELIVERY_STATUS_CONFIG = {
    OPENED:     { label: 'Opened',    color: '#2E7D32', bg: '#E8F5E9', icon: '✓✓' },
    DELIVERED:  { label: 'Delivered', color: '#1565C0', bg: '#E3F2FD', icon: '✓'  },
    SENT:       { label: 'Sent',      color: '#E65100', bg: '#FFF3E0', icon: '→'  },
    PENDING:    { label: 'Pending',   color: '#9E9E9E', bg: '#F5F5F5', icon: '⏳' },
    FAILED:     { label: 'Failed',    color: '#C62828', bg: '#FFEBEE', icon: '✗'  },
    NEVER_SENT: { label: 'Not Sent',  color: '#78909C', bg: '#ECEFF1', icon: '—'  },
}

const STATUS_SORT_ORDER = {
    FAILED:     1,
    NEVER_SENT: 2,
    PENDING:    3,
    SENT:       4,
    DELIVERED:  5,
    OPENED:     6,
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const parseActiveStore = (rawStore) => {
    try {
        return rawStore && rawStore.trim() !== '' ? JSON.parse(rawStore) : null
    } catch {
        return null
    }
}

const formatINR = (amount) => {
    const value = Number(amount)
    if (!Number.isFinite(value)) return '—'
    return value.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
}

const formatDateTime = (value) => {
    if (!value) return null
    const parsed = String(value).includes(' ') ? String(value).replace(' ', 'T') : String(value)
    const date = new Date(parsed)
    if (Number.isNaN(date.getTime())) return value
    return date.toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
    })
}

// ─── Sub-components ──────────────────────────────────────────────────────────

const StatusBadge = ({ status }) => {
    const cfg = DELIVERY_STATUS_CONFIG[status] || DELIVERY_STATUS_CONFIG.NEVER_SENT
    return (
        <span
            className={styles.statusBadge}
            style={{ color: cfg.color, backgroundColor: cfg.bg }}
        >
            {cfg.icon}&nbsp;{cfg.label}
        </span>
    )
}

const LogRow = ({ log, channel }) => {
    const statusCfg = DELIVERY_STATUS_CONFIG[log?.deliveryStatus] || DELIVERY_STATUS_CONFIG.NEVER_SENT
    return (
        <div className={styles.logRow}>
            <div className={styles.logChannelBadge}>
                {channel === 'whatsapp' ? <MessageCircle size={11} /> : <CheckCircle2 size={11} />}
                <span>{channel === 'whatsapp' ? 'WhatsApp' : 'PDF Download'}</span>
                <span className={styles.logStatusText} style={{ color: statusCfg.color }}>
                    {statusCfg.label}
                </span>
            </div>
            <div className={styles.logTimes}>
                {log?.sentOn      && <span><strong>Sent:</strong> {formatDateTime(log.sentOn)}</span>}
                {log?.deliveredOn && <span><strong>Delivered:</strong> {formatDateTime(log.deliveredOn)}</span>}
                {log?.openedOn    && <span><strong>Opened:</strong> {formatDateTime(log.openedOn)}</span>}
                {log?.failureReason && (
                    <span className={styles.failureReason}>
                        <CircleAlert size={11} /> {log.failureReason}
                    </span>
                )}
                {log?.retryCount > 0 && (
                    <span className={styles.retryCount}>Retries: {log.retryCount}</span>
                )}
            </div>
        </div>
    )
}

const StaffDeliveryRow = ({ staff, onSend, isSending }) => {
    const [expanded, setExpanded] = useState(false)

    const cfg = DELIVERY_STATUS_CONFIG[staff?.overallStatus] || DELIVERY_STATUS_CONFIG.NEVER_SENT

    const isPrimary = staff?.overallStatus === 'NEVER_SENT' || staff?.overallStatus === 'FAILED'
    const actionLabel = staff?.overallStatus === 'FAILED'
        ? 'Retry WhatsApp'
        : staff?.overallStatus === 'NEVER_SENT'
            ? 'Send on WhatsApp'
            : 'Resend'

    const waLogs = staff?.whatsapp?.logs || []
    const dlLogs = staff?.download?.logs  || []
    const hasLogs = waLogs.length > 0 || dlLogs.length > 0

    return (
        <div className={styles.staffRow}>
            <div className={styles.staffHead}>
                <div className={styles.identityBlock}>
                    <FaCircleUser className={styles.avatarIcon} />
                    <div className={styles.identityText}>
                        <strong>{staff?.staffName || 'Staff'}</strong>
                        <span>{staff?.designationLabel || '—'}</span>
                    </div>
                </div>
                <StatusBadge status={staff?.overallStatus} />
            </div>

            <div className={styles.staffMeta}>
                <span>Net Pay: <strong>₹{formatINR(staff?.netPay)}</strong></span>
                {staff?.latestSentOn && (
                    <span>Last sent: {formatDateTime(staff.latestSentOn)}</span>
                )}
                {staff?.totalAttempts > 0 && (
                    <span>Attempts: {staff.totalAttempts}</span>
                )}
            </div>

            <div className={styles.staffActions}>
                <button
                    type="button"
                    className={isPrimary ? styles.primaryBtn : styles.secondaryBtn}
                    onClick={() => onSend(staff?.staffIdentifier, staff?.staffName)}
                    disabled={isSending}
                >
                    {isSending ? (
                        <><LoaderCircle size={13} className={styles.spinIcon} /> Sending...</>
                    ) : (
                        <><Send size={13} /> {actionLabel}</>
                    )}
                </button>

                {hasLogs && (
                    <button
                        type="button"
                        className={styles.ghostBtn}
                        onClick={() => setExpanded((prev) => !prev)}
                    >
                        {expanded ? <><ChevronUp size={13} /> Hide logs</> : <><ChevronDown size={13} /> View logs</>}
                    </button>
                )}
            </div>

            {expanded && hasLogs && (
                <div className={styles.logPanel}>
                    {waLogs.map((log) => (
                        <LogRow key={log.deliveryIdentifier} log={log} channel="whatsapp" />
                    ))}
                    {dlLogs.map((log) => (
                        <LogRow key={log.deliveryIdentifier} log={log} channel="download" />
                    ))}
                </div>
            )}
        </div>
    )
}

// ─── Main Screen ──────────────────────────────────────────────────────────────

const PayslipDeliveryStatusScreen = () => {
    const navigate    = useNavigate()
    const { runIdentifier } = useParams()
    const authCtx     = useContext(AuthContext)
    const asCtx       = useContext(ActiveStoreContext)
    const activeStore = parseActiveStore(asCtx?.activeStore)
    const token       = authCtx?.token

    const [deliveryData,   setDeliveryData]   = useState(null)
    const [isLoading,      setIsLoading]       = useState(true)
    const [sendingStaffId, setSendingStaffId]  = useState(null)
    const [isSendingAll,   setIsSendingAll]    = useState(false)

    const fetchDeliveryStatus = useCallback(async () => {
        if (!token || !activeStore?.storeIdentifier) return

        try {
            setIsLoading(true)

            const response = await fetch(BASE_URL_API + 'getPayslipDeliveryStatus', {
                method: 'POST',
                body: JSON.stringify({
                    token,
                    storeIdentifier: activeStore.storeIdentifier,
                    runIdentifier,
                }),
            })

            const respo = await response.json()

            if (respo?.status === 'success') {
                setDeliveryData(respo.response)
            } else {
                if (respo?.message === 'tokenExpired') { authCtx.logout(); return }
                toast.error(respo?.message || 'Failed to load delivery status.')
                navigate(-1)
            }
        } catch {
            toast.error('Network error.')
        } finally {
            setIsLoading(false)
        }
    }, [token, activeStore?.storeIdentifier, runIdentifier, authCtx, navigate])

    useEffect(() => {
        fetchDeliveryStatus()
    }, [fetchDeliveryStatus])

    const sendPayslipWhatsapp = useCallback(async (staffIdentifier, staffName) => {
        if (!token || !activeStore?.storeIdentifier || !runIdentifier || !staffIdentifier) return

        try {
            setSendingStaffId(staffIdentifier)

            const response = await fetch(BASE_URL_API + 'sendPayslipOnWhatsapp', {
                method: 'POST',
                body: JSON.stringify({
                    token,
                    storeIdentifier: activeStore.storeIdentifier,
                    runIdentifier,
                    staffIdentifier,
                }),
            })

            const respo = await response.json()

            if (respo?.message === 'tokenExpired') { authCtx.logout(); return }

            if (respo?.status === 'success') {
                toast.success(`Payslip sent to ${staffName || 'staff'} on WhatsApp.`)
                fetchDeliveryStatus()
            } else if (respo?.errorCode === 'PAY_005') {
                toast.warn(`No WhatsApp number on file for ${staffName || 'this staff member'}.`)
            } else if (respo?.errorCode === 'WA_001') {
                toast.error(`WhatsApp delivery failed for ${staffName || 'staff'}. Please retry.`)
            } else {
                toast.error(respo?.message || 'Send failed.')
            }
        } catch {
            toast.error('Network error.')
        } finally {
            setSendingStaffId(null)
        }
    }, [token, activeStore?.storeIdentifier, runIdentifier, authCtx, fetchDeliveryStatus])

    const handleSendAllUnsent = useCallback(async () => {
        if (!deliveryData?.staffDelivery) return

        const unsentStaff = deliveryData.staffDelivery.filter(
            (s) => s.overallStatus === 'NEVER_SENT' || s.overallStatus === 'FAILED'
        )

        if (unsentStaff.length === 0) {
            toast.info('No unsent staff to send payslips to.')
            return
        }

        let sent = 0, failed = 0, noPhone = 0

        try {
            setIsSendingAll(true)

            for (const staff of unsentStaff) {
                try {
                    const response = await fetch(BASE_URL_API + 'sendPayslipOnWhatsapp', {
                        method: 'POST',
                        body: JSON.stringify({
                            token,
                            storeIdentifier: activeStore.storeIdentifier,
                            runIdentifier,
                            staffIdentifier: staff.staffIdentifier,
                        }),
                    })

                    const respo = await response.json()

                    if (respo?.message === 'tokenExpired') { authCtx.logout(); return }

                    if (respo?.status === 'success') {
                        sent += 1
                    } else if (respo?.errorCode === 'PAY_005') {
                        noPhone += 1
                    } else {
                        failed += 1
                    }
                } catch {
                    failed += 1
                }

                await new Promise((resolve) => setTimeout(resolve, 300))
            }

            const msg = `Sent: ${sent}, Failed: ${failed}, No phone: ${noPhone}`
            if (failed > 0 || noPhone > 0) {
                toast.warn(msg)
            } else {
                toast.success(msg)
            }

            fetchDeliveryStatus()
        } catch {
            toast.error('Network error.')
        } finally {
            setIsSendingAll(false)
        }
    }, [deliveryData, token, activeStore?.storeIdentifier, runIdentifier, authCtx, fetchDeliveryStatus])

    // ─── Derived ────────────────────────────────────────────────────────────

    const summary    = deliveryData?.summary || {}
    const totalStaff = Number(summary.totalStaff || 0)
    const reached    = Number(summary.reachedCount || 0)
    const reachPct   = totalStaff > 0 ? Math.round((reached / totalStaff) * 100) : 0
    const neverSent  = Number(summary.neverSent || 0)

    const sortedStaff = deliveryData?.staffDelivery
        ? [...deliveryData.staffDelivery].sort(
            (a, b) =>
                (STATUS_SORT_ORDER[a.overallStatus] ?? 7) -
                (STATUS_SORT_ORDER[b.overallStatus] ?? 7)
        )
        : []

    // ─── Render ─────────────────────────────────────────────────────────────

    return (
        <div className={styles.page}>
            {/* Header */}
            <div className={styles.header}>
                <button className={styles.backBtn} type="button" onClick={() => navigate(-1)}>
                    <ChevronLeft size={18} />
                </button>
                <div className={styles.headerMeta}>
                    <h1 className={styles.headerTitle}>Payslip Delivery</h1>
                    <p className={styles.headerSub}>
                        {deliveryData?.cycleLabel || 'Loading...'}
                        {deliveryData?.runStatus && (
                            <span className={styles.runStatusPill}>{deliveryData.runStatus}</span>
                        )}
                    </p>
                </div>
                <button
                    type="button"
                    className={styles.refreshBtn}
                    onClick={fetchDeliveryStatus}
                    disabled={isLoading}
                    title="Refresh"
                >
                    <RefreshCw size={16} className={isLoading ? styles.spinIcon : undefined} />
                </button>
            </div>

            {isLoading && !deliveryData ? (
                <div className={styles.loaderCard}>
                    <LoaderCircle size={20} className={styles.spinIcon} />
                    <span>Loading delivery status...</span>
                </div>
            ) : !deliveryData ? (
                <div className={styles.inlineAlertWarn}>
                    <AlertTriangle size={16} />
                    <span>Unable to load delivery data.</span>
                </div>
            ) : (
                <>
                    {/* Summary Strip */}
                    <section className={styles.summarySection}>
                        <div className={styles.summaryGrid}>
                            <div className={styles.summaryCard}>
                                <Users size={16} className={styles.summaryIcon} />
                                <strong>{reached}/{totalStaff}</strong>
                                <span>Reached</span>
                            </div>
                            <div className={styles.summaryCard}>
                                <CheckCheck size={16} className={styles.summaryIconGreen} />
                                <strong>{summary.opened ?? 0}</strong>
                                <span>Opened</span>
                            </div>
                            <div className={styles.summaryCard}>
                                <CheckCircle2 size={16} className={styles.summaryIconBlue} />
                                <strong>{summary.delivered ?? 0}</strong>
                                <span>Delivered</span>
                            </div>
                            <div className={styles.summaryCard}>
                                <Send size={16} className={styles.summaryIconOrange} />
                                <strong>{summary.sent ?? 0}</strong>
                                <span>Sent</span>
                            </div>
                            <div className={styles.summaryCard}>
                                <CircleAlert size={16} className={styles.summaryIconRed} />
                                <strong>{summary.failed ?? 0}</strong>
                                <span>Failed</span>
                            </div>
                        </div>

                        <div className={styles.progressWrap}>
                            <div className={styles.progressBar}>
                                <div
                                    className={styles.progressFill}
                                    style={{ width: `${reachPct}%` }}
                                />
                            </div>
                            <span className={styles.progressLabel}>{reachPct}% reached</span>
                        </div>
                    </section>

                    {/* Send All Unsent */}
                    {neverSent > 0 && (
                        <section className={styles.sendAllCard}>
                            <div className={styles.sendAllInfo}>
                                <AlertTriangle size={15} className={styles.sendAllIcon} />
                                <span>
                                    {neverSent} staff {neverSent === 1 ? 'has' : 'have'} not received their payslip yet.
                                </span>
                            </div>
                            <button
                                type="button"
                                className={styles.primaryBtn}
                                onClick={handleSendAllUnsent}
                                disabled={isSendingAll}
                            >
                                {isSendingAll ? (
                                    <><LoaderCircle size={14} className={styles.spinIcon} /> Sending...</>
                                ) : (
                                    <><Send size={14} /> Send payslip to {neverSent} unsent staff</>
                                )}
                            </button>
                        </section>
                    )}

                    {/* Staff List */}
                    <section className={styles.listSection}>
                        {sortedStaff.length === 0 ? (
                            <div className={styles.emptyState}>
                                <Users size={32} className={styles.emptyIcon} />
                                <p>No staff delivery data available.</p>
                            </div>
                        ) : (
                            sortedStaff.map((staff) => (
                                <StaffDeliveryRow
                                    key={staff.staffIdentifier}
                                    staff={staff}
                                    onSend={sendPayslipWhatsapp}
                                    isSending={sendingStaffId === staff.staffIdentifier || isSendingAll}
                                />
                            ))
                        )}
                    </section>
                </>
            )}
        </div>
    )
}

export default PayslipDeliveryStatusScreen
